import type { Locale } from '@rozman/shared';

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** i18n key, never a sentence — the UI resolves it in the customer's locale. */
    readonly code: string,
    readonly issues?: Array<{ path: string; code: string }>,
    readonly context?: Record<string, unknown>,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  issues?: Array<{ path: string; code: string }>;
  context?: Record<string, unknown>;
}

let accessToken: string | null = null;
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let body: ErrorBody = {};
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      body = {};
    }
    throw new ApiError(
      response.status,
      body.error ?? (response.status === 429 ? 'errors.rateLimited' : 'errors.generic'),
      body.issues,
      body.context,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const qs = (params: Record<string, string | number | boolean | string[] | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export interface ProductCard {
  id: string;
  slug: string;
  name: string;
  nameIsFallback: boolean;
  categorySlug: string | null;
  minPriceGross: number | null;
  maxPriceGross: number | null;
  variantCount: number;
  imageCount: number;
  image: { thumb: string | null; card: string | null; full: string | null; alt: string } | null;
  availability: { state: 'in_stock' | 'low_stock' | 'made_to_order' | 'unavailable'; quantity: number | null; leadTimeDays: [number, number] | null };
  colors: Array<{ slug: string; label: string; hex: string[] }>;
  supplierCode: string;
}

export interface FacetBucket {
  value: string;
  label: string;
  count: number;
  hex?: string[];
}

export interface CatalogResponse {
  items: ProductCard[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** `capped` means the total is a floor, not a count — the UI must say "500+". */
  meta: { capped: boolean; queryTooShort: boolean; minQueryLength: number };
  facets: {
    colors: FacetBucket[];
    fabricFamilies: FacetBucket[];
    sizes: FacetBucket[];
    priceRange: { min: number; max: number };
  };
}

export interface ProductOptionValue {
  slug: string;
  label: string;
  hex: string[];
  truncated: boolean;
}

export interface ProductDetail {
  id: string;
  slug: string;
  name: string;
  nameIsFallback: boolean;
  description: string;
  descriptionIsFallback: boolean;
  category: { slug: string; name: string } | null;
  supplier: { code: string; name: string };
  /** `simple` renders no selector at all — 274 Adams products have exactly one variant. */
  mode: 'simple' | 'configurator';
  /** `variantId` is null for the product-level set, otherwise the variant it shows. */
  images: Array<{
    thumb: string | null;
    card: string | null;
    full: string | null;
    width: number | null;
    height: number | null;
    alt: string;
    variantId: string | null;
  }>;
  options: Array<{ axisSlug: string; label: string; widget: string; values: ProductOptionValue[] }>;
  variants: Array<{
    id: string;
    sku: string;
    ean: string | null;
    optionValues: Record<string, string>;
    priceGross: number | null;
    priceNet: number | null;
    availability: ProductCard['availability'];
    parcels: unknown;
    weightKg: number | null;
  }>;
  specification: {
    widthCm: number | null;
    depthCm: number | null;
    heightCm: number | null;
    weightKg: number | null;
    packageCount: number | null;
    parcels: Array<{ index: number; heightCm: number | null; widthCm: number | null; depthCm: number | null; weightKg: number | null }>;
    shippingVolumeM3: number | null;
    ean: string | null;
    attributes: Record<string, string | null>;
  };
  /** Absent, not empty, on the products with no mechanism media. */
  mechanism: { image: string | null; gif: string | null } | null;
  leadTimeDays: [number, number];
  minPriceGross: number | null;
  maxPriceGross: number | null;
  seoTitle: string | null;
  seoDescription: string | null;
}

export interface CategoryNode {
  id: string;
  slug: string;
  name: string;
  position: number;
  productCount: number;
  children: CategoryNode[];
  weight?: 'xl' | 'lg' | 'md' | 'sm';
  /** A representative photograph for the homepage tile. */
  image?: { card: string | null; full: string | null; alt: string } | null;
}

export interface FabricFamily {
  slug: string;
  label: string;
  productCount: number;
  colors: Array<{ slug: string; label: string; hex: string[]; twoTone: boolean }>;
}

export interface HomePayload {
  hero: CategoryNode | null;
  categories: CategoryNode[];
  featured: ProductCard[];
  newArrivals: ProductCard[];
  fabricFamilies: FabricFamily[];
}

export interface CartQuote {
  lines: Array<{
    variantId: string;
    productSlug: string;
    name: string;
    sku: string;
    options: Array<{ axis: string; label: string; value: string }>;
    quantity: number;
    unitPriceGross: number;
    lineTotalGross: number;
    availability: ProductCard['availability'];
    supplierCode: string;
    supplierName: string;
    image: string | null;
  }>;
  shipments: Array<{
    supplierCode: string;
    supplierName: string;
    profileCode: string;
    parcelCount: number;
    chargeableWeightKg: number;
    volumeM3: number;
    totalCost: number;
    freeShippingApplied: boolean;
    deliveryEstimateDays: [number, number];
  }>;
  itemsTotalGross: number;
  shippingTotalGross: number;
  discountGross: number;
  totalGross: number;
  totalNet: number;
  totalVat: number;
  currency: string;
  promoCode: string | null;
}

export const catalogApi = {
  list: (params: Record<string, string | number | string[] | undefined>) =>
    api<CatalogResponse>(`/catalog/products${qs(params)}`),
  detail: (slug: string, locale: Locale) => api<ProductDetail>(`/catalog/products/${slug}${qs({ locale })}`),
  categories: (locale: Locale) => api<CategoryNode[]>(`/catalog/categories${qs({ locale })}`),
  home: (locale: Locale) => api<HomePayload>(`/catalog/home${qs({ locale })}`),
  fabrics: (locale: Locale) => api<FabricFamily[]>(`/catalog/fabrics${qs({ locale })}`),
  suggest: (q: string, locale: Locale) =>
    api<{ products: Array<{ slug: string; name: string; priceGross: number | null; thumb: string | null }> }>(
      `/catalog/suggest${qs({ q, locale })}`,
    ),
};

export const cartApi = {
  quote: (items: Array<{ variantId: string; quantity: number }>, locale: Locale, promoCode?: string | null) =>
    api<CartQuote>('/cart/quote', { method: 'POST', body: JSON.stringify({ items, locale, promoCode }) }),
};

export interface CustomerOrder {
  id: string;
  number: string;
  status: string;
  placedAt: string;
  totalGross: number;
  items: Array<{ id: string; nameSnapshot: string; quantity: number; lineTotalGross: number; supplierCode: string }>;
  shipments: Array<{ supplierName: string; costGross: number; deliveryMinDays: number; deliveryMaxDays: number; trackingNumber: string | null }>;
}

export const accountApi = {
  me: () => api<{ id: string; email: string; firstName: string; lastName: string; role: string; locale: string }>('/auth/me'),
  orders: (page = 1) =>
    api<{ items: CustomerOrder[]; total: number; page: number; pageSize: number; totalPages: number }>(
      `/orders${qs({ page, pageSize: 10 })}`,
    ),
  login: (email: string, password: string) =>
    api<{ accessToken: string; refreshToken: string; user: { email: string; firstName: string } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  exportData: () => api<Record<string, unknown>>('/compliance/my-data'),
  erase: () => api<{ erased: string[]; retained: string[] }>('/compliance/erase-me', { method: 'POST' }),
};

export const contentApi = {
  page: (slug: string, locale: Locale) =>
    api<{ slug: string; title: string; body: string; version: string; isFallback?: boolean }>(
      `/compliance/pages/${slug}${qs({ locale })}`,
    ),
  consent: (body: { necessary: true; analytics: boolean; marketing: boolean; anonymousId?: string }) =>
    api<unknown>('/compliance/consent', { method: 'POST', body: JSON.stringify(body) }),
};
